import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

// Formato con la moneda real de la tienda analizada (no se convierte a la de la empresa).
const formatPrice = (value, lang, currency) => {
  if (value === null || value === undefined) return '\u2014';
  const amount = Number(value) || 0;
  if (currency) {
    try {
      return new Intl.NumberFormat(lang || 'es', { style: 'currency', currency }).format(amount);
    } catch (err) {
      // moneda no reconocida por el navegador: se muestra el numero con el codigo
      return `${currency} ${amount.toFixed(2)}`;
    }
  }
  return new Intl.NumberFormat(lang || 'es', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

function StatCard({ label, value, note }) {
  return (
    <div className="bg-surfaceAlt border border-edge rounded-xl p-4">
      <p className="text-muted text-xs mb-1">{label}</p>
      <p className="text-white text-xl font-bold break-words">{value}</p>
      {note && <p className="text-muted text-[11px] mt-1">{note}</p>}
    </div>
  );
}

function ProductTable({ title, rows, emptyText, lang, currency, t }) {
  return (
    <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6 mb-6">
      <h3 className="text-white text-base font-semibold mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-muted text-sm">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted text-xs text-left border-b border-edge">
                <th className="py-2 pr-3 font-medium">{t('competitor.colProduct')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('competitor.colPrice')}</th>
                <th className="py-2 pr-3 font-medium">{t('competitor.colDate')}</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={`${row.url || row.title}-${i}`} className="border-b border-edge last:border-0">
                  <td className="py-2 pr-3 text-white">
                    {row.title || '\u2014'}
                    {row.vendor && <span className="block text-muted text-[11px]">{row.vendor}</span>}
                  </td>
                  <td className="py-2 pr-3 text-white text-right whitespace-nowrap">{formatPrice(row.price, lang, currency)}</td>
                  <td className="py-2 pr-3 text-muted whitespace-nowrap">
                    {row.published_at ? new Date(row.published_at).toLocaleDateString(lang) : '\u2014'}
                  </td>
                  <td className="py-2 text-right">
                    {row.url && (
                      <a href={row.url} target="_blank" rel="noopener noreferrer nofollow" className="text-accent text-xs">
                        {t('competitor.viewProduct')} {'\u2197'}
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CompetitorScan({ token, onAuthError }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [url, setUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const scan = async (e) => {
    e.preventDefault();
    if (!url.trim() || scanning) return;
    setScanning(true);
    setError('');
    setResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/tools/competitor-scan`,
        { url: url.trim() },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 45000 }
      );
      setResult(response.data.data);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onAuthError();
        return;
      }
      const detail = err.response && err.response.data && err.response.data.detail;
      const code = detail && typeof detail === 'object' ? detail.code : null;
      if (code) setError(t(`competitor.errors.${code}`, { defaultValue: t('competitor.errors.unexpected') }));
      else if (typeof detail === 'string') setError(detail);
      else setError(t('competitor.errors.network'));
    } finally {
      setScanning(false);
    }
  };

  const cur = result ? result.currency : null;

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-white text-2xl font-bold">{t('competitor.title')}</h2>
        <p className="text-muted text-sm mt-1">{t('competitor.subtitle')}</p>
      </div>

      <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6 mb-6">
        <form onSubmit={scan} className="flex flex-col sm:flex-row gap-3 mb-2">
          <input
            type="text"
            inputMode="url"
            autoComplete="off"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('competitor.placeholder')}
            className="flex-1 bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
          />
          <button
            type="submit"
            disabled={scanning || !url.trim()}
            className={`bg-accent text-panel font-bold text-sm px-5 py-2 rounded-lg cursor-pointer ${scanning || !url.trim() ? 'opacity-70' : ''}`}
          >
            {scanning ? t('competitor.scanning') : t('competitor.button')}
          </button>
        </form>
        <p className="text-muted text-[11px]">{t('competitor.limitNote')}</p>
      </div>

      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-4 mb-6">{error}</div>
      )}

      {result && (
        <>
          <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-4">
              <h3 className="text-white text-lg font-semibold break-all">{result.store_name || result.store}</h3>
              <span className="text-muted text-xs">
                {result.store}{cur ? ` \u00b7 ${cur}` : ''}
                {result.cached ? ` \u00b7 ${t('competitor.cachedNote', { count: result.cache_age_minutes || 0 })}` : ''}
              </span>
            </div>

            {!cur && (
              <div className="bg-surfaceAlt border border-edge text-muted text-xs rounded-lg p-3 mb-4">
                {t('competitor.currencyUnknown')}
              </div>
            )}
            {cur && result.currency_matches === false && (
              <div className="bg-surfaceAlt border border-edge text-muted text-xs rounded-lg p-3 mb-4">
                {t('competitor.currencyDiff', { store: cur, tenant: result.tenant_currency })}
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                label={t('competitor.products')}
                value={result.total_products}
                note={result.truncated ? t('competitor.productsTruncated') : null}
              />
              <StatCard
                label={t('competitor.avgPrice')}
                value={formatPrice(result.avg_price, lang, cur)}
                note={`${t('competitor.medianPrice')}: ${formatPrice(result.median_price, lang, cur)}`}
              />
              <StatCard
                label={t('competitor.range')}
                value={`${formatPrice(result.min_price, lang, cur)} / ${formatPrice(result.max_price, lang, cur)}`}
              />
              <StatCard
                label={t('competitor.soldOut')}
                value={result.availability_known > 0 ? result.sold_out_count : '\u2014'}
                note={result.availability_known > 0
                  ? t('competitor.soldOutOf', { count: result.sold_out_count, known: result.availability_known })
                  : t('competitor.stockUnknown')}
              />
            </div>
            {result.excluded_count > 0 && (
              <p className="text-muted text-[11px] mt-3" title={(result.excluded_examples || []).join(' \u00b7 ')}>
                {t('competitor.excluded', { count: result.excluded_count })}
              </p>
            )}
            <p className="text-muted text-[11px] mt-2">{t('competitor.priceNote')}</p>
          </div>

          <ProductTable
            title={t('competitor.latestTitle')}
            rows={result.latest || []}
            emptyText={t('competitor.noLatest')}
            lang={lang}
            currency={cur}
            t={t}
          />

          <ProductTable
            title={t('competitor.soldOutTitle')}
            rows={result.sold_out || []}
            emptyText={t('competitor.noSoldOut')}
            lang={lang}
            currency={cur}
            t={t}
          />
          <p className="text-muted text-[11px] -mt-3 mb-6">{t('competitor.soldOutNote')}</p>
        </>
      )}
    </div>
  );
}

export default CompetitorScan;
